import { AxiosInstance, AxiosResponse } from "axios";
import ApiPath from "src/api/apiPath.json";
import { ApiResponse, Sort } from "../public-types";
import { getBuildingFloorsAsync, getBuildingRoomsAsync } from "./building-api";
import { BuildingFloorModel, BuildingPropertyListData, BuildingRoomsByOwnerListData, BuildingUpdateResponseData, DeleteType } from "./building-types";

// 페이지네이션 전량 조회용 size (product-api 컨벤션과 동일)
const FETCH_ALL_SIZE = 99999;

/*
  RPB07. 건물 프로퍼티 목록
*/
export async function getPropertiesAsync(
  axios: AxiosInstance,
  params?: {
    buildingId: number;
  },
): Promise<AxiosResponse<ApiResponse<BuildingPropertyListData>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const path = ApiPath.building.property.replace("{id}", params.buildingId.toString());
  return await axios.get<ApiResponse<BuildingPropertyListData>>(path);
}

/*
  건물 프로퍼티(층/호) 목록 조회 - 리팩토링 API 조합 버전
  RPBP01(층 목록) + RPBP05(층별 호 목록)을 조합하여
  기존 프로퍼티 트리(BuildingPropertyListData) 형태로 반환한다.
  (헤비한 RPB07 프로퍼티 API 대체)
*/
export async function getBuildingFloorRoomTreeAsync(
  axios: AxiosInstance,
  params?: {
    buildingId: number;
  },
): Promise<AxiosResponse<ApiResponse<BuildingPropertyListData>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const { buildingId } = params;

  // 층 목록 조회
  const floorsResponse = await getBuildingFloorsAsync(axios, { buildingId, size: FETCH_ALL_SIZE });
  const floors = floorsResponse.data?.data?.content || [];

  // 각 층의 호 목록을 병렬 조회
  const roomsResponses = await Promise.all(
    floors.map((floor) => getBuildingRoomsAsync(axios, { buildingId, floorId: Number(floor.id), size: FETCH_ALL_SIZE })),
  );

  // 기존 프로퍼티 트리(BuildingFloorModel[]) 형태로 변환
  const buildingFloorList: Array<BuildingFloorModel> = floors.map((floor, index) => {
    const rooms = roomsResponses[index]?.data?.data?.content || [];
    return {
      id: Number(floor.id),
      floorNum: floor.floor,
      floorName: floor.name,
      buildingRoomList: rooms.map((room) => ({
        id: Number(room.id),
        roomNum: Number(room.number || 0),
        roomName: room.name,
        leasableAreaNet: room.leasableAreaNet,
        leasableArea: room.leasableArea,
        rent: room.rent,
        ownerId: room.owner?.id,
        ownerName: room.owner?.name,
      })),
    };
  });

  return {
    ...floorsResponse,
    data: {
      ...floorsResponse.data,
      data: { content: buildingFloorList },
    },
  } as AxiosResponse<ApiResponse<BuildingPropertyListData>>;
}

/* 
  CPB08. 건물 프로퍼티 등록
*/
export async function createPropertyAsync(
  axios: AxiosInstance,
  params?: {
    buildingId: number;
    buildingFloorList: Array<BuildingFloorModel>;
  },
): Promise<AxiosResponse<ApiResponse<BuildingUpdateResponseData>>> {
  const { buildingId, buildingFloorList } = params || {};
  if (!buildingId) {
    throw Error("buildingId is not found.");
  }
  if ((buildingFloorList || []).length === 0) {
    throw Error("buildingFloorList is not found.");
  }
  const path = ApiPath.building.property.replace("{id}", buildingId.toString());
  return await axios.post(path, {
    building: {
      buildingFloorList,
    },
  });
}

/* 
  CPB09. 건물 프로퍼티 수정
*/
export async function updatePropertyAsync(
  axios: AxiosInstance,
  params?: {
    buildingId: number;
    buildingFloorList: Array<BuildingFloorModel>;
  },
): Promise<AxiosResponse<ApiResponse<BuildingUpdateResponseData>>> {
  const { buildingId, buildingFloorList } = params || {};
  if (!buildingId) {
    throw Error("buildingId is not found.");
  }
  if ((buildingFloorList || []).length === 0) {
    throw Error("buildingFloorList is not found.");
  }
  const path = ApiPath.building.property.replace("{id}", buildingId.toString());
  return await axios.put(path, {
    building: {
      buildingFloorList,
    },
  });
}

/* 
  CPB10. 건물 프로퍼티 삭제
*/
export async function deletePropertyAsync(
  axios: AxiosInstance,
  params?: {
    buildingId: number;
    deleteType: DeleteType;
    ids: Array<number>; // 삭제타입 대상 id
  },
): Promise<AxiosResponse<ApiResponse<BuildingUpdateResponseData>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.deleteType) {
    throw Error("deleteType is not found.");
  }
  if ((params?.ids || []).length === 0) {
    throw Error("roomIds is not found.");
  }
  const path = ApiPath.building.property.replace("{id}", params?.buildingId.toString());

  return await axios.delete(path, {
    data: {
      deleteType: params.deleteType,
      ids: params.ids,
    },
  });
}

/* 
  RPB19. 소유주별 보유 호실 목록
*/
export async function getRoomsByOwnerAsync(
  axios: AxiosInstance,
  params?: {
    ownerId: string;
    page?: number;
    size?: number;
    sort?: Sort;
  },
): Promise<AxiosResponse<ApiResponse<BuildingRoomsByOwnerListData>>> {
  if (!params?.ownerId) {
    throw Error("ownerId is not found.");
  }

  const path = ApiPath.building.room.list.replace("{ownerId}", params.ownerId.toString());
  return await axios.get<ApiResponse<BuildingRoomsByOwnerListData>>(path, {
    params,
  });
}
